import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { withDateRange } from '@/helpers/dateRangeFilename';

// Downloads a CSV of per-matter credit usage (pages + credits) for every matter in the org,
// scoped to the given date range.
export const useDownloadMatterCreditStats = () => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadMatterCreditStats = async ({
    organisationId,
    startDate,
    endDate,
    matterId,
  }: {
    organisationId: string;
    startDate?: Date;
    endDate?: Date;
    matterId?: string;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: { organisationId, startDate, endDate, matterId },
    });

    const response = await fetch(`${APIBaseChronos}/api/usage/credit-by-matter/download`, fetchConfig);
    if (!response.ok) {
      throw new Error('Failed to download credit usage by matter');
    }

    const blob = await response.blob();
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: withDateRange('matter-credit-usage', 'csv', startDate, endDate),
    });
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const { mutate: fetchDownload, isLoading } = useMutation(downloadMatterCreditStats, {
    onSuccess: () => {
      trackCustomEvent(TrackingEventsV2.UsageDashboardDownloadCreditBreakdown);
    },
    onError: () => {
      toast.error('Failed to download credit usage by matter. Please try again.');
    },
  });

  return { fetchDownload, isLoading };
};
